import { computed, ref } from 'vue';
import { defineStore } from 'pinia';
import { useRoute } from 'vue-router';
import { keyBy, mapValues } from 'lodash-es';
import { createStorage } from '@aviata/storage';
import { useQueryClient } from '@tanstack/vue-query';

import { hotOffersApi } from '@/entities/hot-offers';

import { mapOffersResults } from '@/shared/lib';
import { defaultDepartureCityCode } from '@/shared/config';

import { HOT_OFFERS_DEPARTURE_CITY_STORAGE_KEY } from '../config';

const storage = createStorage(localStorage);

export const useHotOffersStore = defineStore('hot-offers', () => {
  const route = useRoute();
  const queryClient = useQueryClient();

  const origCityCode = ref(
    (route.params.origCity as string) ||
      storage.get(
        HOT_OFFERS_DEPARTURE_CITY_STORAGE_KEY,
        defaultDepartureCityCode,
      ),
  );
  const destCountryCode = ref((route.params.destCountry as string) || null);

  const { hotCountries, isCountriesLoading } =
    hotOffersApi.useHotCountriesQuery({ origCityCode });

  const origCitiesByCode = computed(() =>
    mapValues(keyBy(hotCountries.value, 'origin.city.code'), 'origin.city'),
  );

  const origCity = computed(() => origCitiesByCode.value[origCityCode.value]);

  const { hotCities, isCitiesLoading } = hotOffersApi.useHotCitiesQuery({
    origCityCode,
    destCountryCode,
  });

  const hotCitiesCodes = computed(() => {
    return hotCities.value.map(
      (result: ReturnType<typeof mapOffersResults>) =>
        result.destination.city.code,
    );
  });

  const countryCodeList = computed(() => {
    return hotCountries.value.map(item => item.destination.country.code);
  });

  const destinationCountryIndex = computed(() => {
    const countryIndex = countryCodeList.value.indexOf(
      destCountryCode.value as string,
    );
    return countryIndex > 0 ? countryIndex : 0;
  });

  function changeOrigCityCode(cityCode: string) {
    origCityCode.value = cityCode;
    storage.set(HOT_OFFERS_DEPARTURE_CITY_STORAGE_KEY, cityCode);
  }

  function changeDestCountryCode(countryCode: string) {
    destCountryCode.value = countryCode;
  }

  return {
    isCountriesLoading,
    isCitiesLoading,
    countryCodeList,
    destinationCountryIndex,
    origCityCode,
    destCountryCode,
    changeDestCountryCode,
    changeOrigCityCode,
    hotCountries,
    hotCities,
    hotCitiesCodes,
    origCity,
  };
});
